"use client";

import * as React from "react";
import Image from "next/image";
import { Plus } from "lucide-react";
import { useToggleToast } from "@/lib/toast";

type FollowVariant = "default" | "light";

/**
 * Leading-glyph presets.
 *
 * `hero-section.tsx` carried a byte-identical *second* FollowButton with no
 * state and no `onClick` — visually complete, functionally dead. QA filed it on
 * `/property` ("關注按鈕沒有反應"). Its three looks are folded in here rather
 * than repaired in place so there is one follow control in the product, and the
 * metrics below reproduce that component's rendering exactly so the four pages
 * using it (`/property`, `/wealth-management`, the two stock360 listings) get
 * behaviour without a visual diff.
 *
 * `dot` is the original shared metric — keep it the default so the existing
 * call sites are untouched.
 */
type FollowIcon = "dot" | "plus" | "image" | "none";

const ICON_METRICS: Record<FollowIcon, { gap: string; padding: string }> = {
  dot: { gap: "gap-[2px]", padding: "3px 5px" },
  plus: { gap: "gap-[1px]", padding: "3px 5px" },
  image: { gap: "gap-[1px]", padding: "3px 5px" },
  none: { gap: "gap-[2px]", padding: "3px 6px" },
};

interface FollowButtonProps {
  initialFollowed?: boolean;
  onChange?: (next: boolean) => void;
  className?: string;
  /**
   * default = green border on white bg (for light surfaces)
   * light   = light-green (#dbf0e9) border on dark/transparent bg (used on dark gradients)
   * Active state (when clicked) is identical across variants: bg #e5e5e5, border #e5e5e5, text #000.
   */
  variant?: FollowVariant;
  /** Leading glyph. `none` renders the label as "+ 關注". */
  icon?: FollowIcon;
  /** Accessible name. Defaults to the visible label. */
  ariaLabel?: string;
  /**
   * Controlled state. Omit and the button owns its own — which is what the
   * card call sites want. `/account/following` lifts it because unfollowing
   * there goes through a confirmation dialog and must not flip until the
   * reader confirms.
   */
  followed?: boolean;
  /**
   * Called INSTEAD of toggling when the control is already followed. Lets a
   * caller interpose a confirmation step; the caller then flips `followed`
   * itself and raises its own toast.
   */
  onRequestUnfollow?: () => void;
}

export function FollowButton({
  initialFollowed = false,
  onChange,
  className,
  variant = "default",
  icon = "dot",
  ariaLabel,
  followed: followedProp,
  onRequestUnfollow,
}: FollowButtonProps) {
  const [ownFollowed, setOwnFollowed] = React.useState(initialFollowed);
  const followed = followedProp ?? ownFollowed;
  const notify = useToggleToast();

  // Nested inside card-wide links (article-header, article-item-card,
  // article-block, first-section-sidebar), so the click must not bubble up and
  // navigate. Mirrors components/columns/authors/authors-grid.tsx — QA asked for
  // the author-follow here to "跟返之前咁樣改", i.e. behave like that one.
  const toggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Unfollowing can be gated behind a confirmation by the caller. Nothing
    // changes and nothing is announced until they say so.
    if (followed && onRequestUnfollow) {
      onRequestUnfollow();
      return;
    }
    const next = !followed;
    if (followedProp === undefined) setOwnFollowed(next);
    onChange?.(next);
    notify.follow(next);
  };

  let border = "1px solid #259c73";
  let bg: string | undefined = "transparent";
  let color = "#259c73";
  /**
   * Hover tint, per variant.
   *
   * A single `hover:bg-[#f5fbf8]` was applied to all of them — which on the
   * dark 電子版訂閱 panel painted a near-white pill behind the `light`
   * variant's near-white (#dbf0e9) label, so the text vanished on hover. QA:
   * "the hover effect on the follow button causing the text is not visible".
   * Each variant now names a tint that keeps its own label legible.
   */
  let hoverBg = "#f5fbf8";

  if (variant === "light") {
    border = "1px solid #dbf0e9";
    bg = undefined;
    color = "#dbf0e9";
    hoverBg = "rgba(255,255,255,0.18)";
  }

  if (followed) {
    border = "1px solid #e5e5e5";
    bg = "#e5e5e5";
    color = "#000";
    hoverBg = "#d6d6d6";
  }

  const metrics = ICON_METRICS[icon];
  const label = followed ? "已關注" : icon === "none" ? "+ 關注" : "關注";

  // The glyph is the unfollowed affordance only — once followed, every variant
  // collapses to the bare "已關注" pill.
  const glyph =
    followed || icon === "none" ? null : icon === "image" ? (
      <Image
        src="/images/category-finance-list/icon-follow.svg"
        alt=""
        width={12}
        height={12}
      />
    ) : icon === "plus" ? (
      <Plus style={{ width: 10, height: 10 }} strokeWidth={3} />
    ) : (
      <Plus className="size-[12px]" strokeWidth={2.5} />
    );

  return (
    <button
      type="button"
      aria-pressed={followed}
      aria-label={ariaLabel}
      onClick={toggle}
      // QA: every 關注 control takes the same hover tint as the 熱門屋苑 chips.
      // The resting colour goes through a custom property, NOT an inline
      // `background-color` — an inline declaration outranks every class, so a
      // `hover:bg-*` written here would never apply.
      className={`inline-flex items-center justify-center ${metrics.gap} rounded-[2px] bg-[color:var(--rest-bg)] transition-colors hover:bg-[color:var(--hover-bg)] ${className ?? ""}`}
      style={
        {
          border,
          ["--rest-bg" as string]: bg ?? "transparent",
          ["--hover-bg" as string]: hoverBg,
          color,
          padding: metrics.padding,
        } as React.CSSProperties
      }
    >
      {glyph}
      <span
        style={{
          fontFamily: "Roboto, 'Noto Sans TC', sans-serif",
          fontSize: "12px",
          lineHeight: "normal",
        }}
      >
        {label}
      </span>
    </button>
  );
}
